import { useState } from 'react'

import { cn } from '@/lib/utils'
import { useBlobUrl } from '@/hooks/useBlobUrl'
import { FxSpinner } from '@/components/fx'
import type { BoxConfiguration, FieldSuggestion, Position } from '@/types/domain'

export interface OverlayBox {
  id: string
  label: string
  position: Position
  tone?: 'default' | 'success' | 'warning' | 'danger'
}

const toneStroke = {
  default: 'stroke-primary',
  success: 'stroke-success',
  warning: 'stroke-warning',
  danger: 'stroke-danger',
} as const

/**
 * Boxes drawn over a page image.
 *
 * **There is no scale factor in this component.** The SVG's `viewBox` is the
 * stored image's own pixel size and the boxes are given in stored
 * coordinates, so the browser does the display scaling — the same arithmetic
 * that is the main source of coordinate bugs in the Flutter version. The
 * image's natural size is read once it loads rather than asked of the API,
 * because it is the authority on what the boxes are relative to.
 *
 * The other two frames — a result box in page-image space vs. a template box
 * in the template page's own, larger frame — are bridged by
 * `templateToPageScale` before boxes reach this component, not here.
 */
export function BoxOverlay({
  imagePath,
  boxes,
  className,
  showLabels = true,
  onBoxClick,
  appearStaggerMs,
}: {
  imagePath: string
  boxes: OverlayBox[]
  className?: string
  showLabels?: boolean
  onBoxClick?: (id: string) => void
  /**
   * Fade each box in, this many milliseconds apart, so a freshly detected set
   * arrives one at a time instead of all at once.
   *
   * The stagger is an `animation-delay`, which means the whole set is rendered
   * in a single pass and the browser does the timing. Revealing them by
   * slicing the array on an interval instead cost one React render per box —
   * forty renders of this SVG inside a second, which stuttered and disturbed
   * every other animation on the screen.
   */
  appearStaggerMs?: number
}) {
  const { url, error } = useBlobUrl(imagePath)
  const [size, setSize] = useState<{ width: number; height: number } | null>(null)

  if (error) {
    return (
      <div className="grid min-h-48 place-items-center rounded-md bg-ground p-8 text-body text-text-muted">
        Seitenbild konnte nicht geladen werden.
      </div>
    )
  }

  if (!url) {
    return (
      <div className="grid min-h-48 place-items-center rounded-md bg-ground p-8">
        <FxSpinner label="Seite wird geladen …" />
      </div>
    )
  }

  return (
    <div className={cn('relative overflow-hidden rounded-sm bg-surface shadow-raised', className)}>
      <img
        src={url}
        alt=""
        className="block w-full"
        onLoad={(event) =>
          setSize({
            width: event.currentTarget.naturalWidth,
            height: event.currentTarget.naturalHeight,
          })
        }
      />
      {size && (
        <svg
          viewBox={`0 0 ${size.width} ${size.height}`}
          className="absolute inset-0 h-full w-full"
          // The rectangles must sit exactly on the pixels they name, so the
          // SVG has to be laid out the way the <img> is — which for a block
          // image at width:100% is a uniform fit.
          preserveAspectRatio="none"
        >
          {boxes.map((box, index) => (
            <g
              key={box.id}
              className={cn(
                onBoxClick && 'cursor-pointer',
                appearStaggerMs != null && 'animate-box-in motion-reduce:animate-none',
              )}
              style={
                appearStaggerMs != null
                  ? { animationDelay: `${index * appearStaggerMs}ms` }
                  : undefined
              }
              onClick={onBoxClick ? () => onBoxClick(box.id) : undefined}
            >
              <rect
                x={box.position.x}
                y={box.position.y}
                width={box.position.width}
                height={box.position.height}
                className={cn('fill-transparent', toneStroke[box.tone ?? 'default'])}
                // Stroke width is in viewBox units, so it thins as the image
                // is displayed smaller. Scaling it with the page keeps a
                // hairline on a 2400px scan visible without being a slab on
                // a thumbnail.
                strokeWidth={Math.max(2, size.width / 600)}
              />
              {showLabels && (
                <text
                  x={box.position.x}
                  y={Math.max(box.position.y - size.width / 300, size.width / 120)}
                  className="fill-primary"
                  style={{ fontSize: size.width / 70 }}
                >
                  {box.label}
                </text>
              )}
            </g>
          ))}
        </svg>
      )}
    </div>
  )
}

/** Tolerates a missing list: the gateway omits `boxConfiguration` on a page
 *  that has none yet, and this is exported, so a caller may hand it whatever
 *  the wire gave them. */
export function boxesFromConfiguration(boxes?: BoxConfiguration[] | null): OverlayBox[] {
  return (boxes ?? [])
    .filter((box): box is BoxConfiguration & { position: Position } => box.position != null)
    .map((box) => ({ id: box.id, label: box.label, position: box.position }))
}

export function boxesFromSuggestions(suggestions: FieldSuggestion[]): OverlayBox[] {
  return suggestions.map((suggestion) => ({
    id: suggestion.id,
    label: suggestion.label,
    position: suggestion.position,
    // A proposal that matched one of the template's existing shared fields
    // is the safe kind; a new field is the one worth a second look.
    tone: suggestion.matchedField ? 'success' : 'default',
  }))
}
